import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { ActivityIndicator, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import * as Crypto from 'expo-crypto';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import AutoSchedulePreview from '@/components/Tasks/AutoSchedulePreview';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import { Query, isSupabaseNetworkError } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    AUTO_SCHEDULE_HORIZONS,
    AUTO_SCHEDULE_DEFAULT_HORIZON,
    AUTO_SCHEDULE_GENERATION_TIMEOUT_MS,
    AUTO_SCHEDULE_ACTIVE_STATUSES,
    AUTO_SCHEDULE_STALE_CODES,
    AUTO_SCHEDULE_EXPIRED_CODES,
    AUTO_SCHEDULE_MODEL_FAILURE_CODES,
    generateAutoSchedule,
    validateAutoSchedule,
    confirmAutoSchedule,
    cancelAutoSchedule,
    addAutoScheduleExclusion,
    removeAutoScheduleExclusion,
} from '@/data/taskAutoSchedule';
import {
    COPY,
    ackKey,
    acknowledgedWarningsPayload,
    buildDraftState,
    computeDraftConflicts,
    confirmLabel,
    draftAdditionCount,
    draftHash,
    gapLine,
    missingAcknowledgments,
    personLabel,
    progressLine,
    readProgress,
    readReceipt,
    requiredAcknowledgments,
    successLine,
    summaryLine,
    useAutoScheduleAccess,
    validatePayload,
} from './autoScheduleModel';

const newId = () => Crypto.randomUUID();
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Consecutive transient poll failures tolerated before surfacing failure —
// ~tens of seconds at poll cadence, well under the 15-min backstop deadline.
const MAX_TRANSIENT_STATUS_ERRORS = 6;
// Logical scope of a generate request — drives idempotent-retry key reuse.
const scopeKeyFor = (teamIDs, eventID, horizonDays) =>
    `${[...(teamIDs || [])].sort().join(',')}|${eventID ?? 'all'}|${horizonDays}`;
// Poll failures that must not kill a run: server-flagged retryable, or an
// uncoded transport/network message (suspended screen, dropped connection).
const isTransientStatusError = (error) => {
    if (!error) return false;
    if (error.retryable) return true;
    if (error.code) return false;
    return isSupabaseNetworkError(error) || /fetch|network|timeout|timed out|load failed|relay|abort|failed to send/i.test(String(error?.message || ''));
};

const makeInitial = () => ({
    phase: 'setup',            // setup|generating|preview|validating|confirmReview|confirming|success|empty|failed|stale|expired|canceled
    horizonDays: AUTO_SCHEDULE_DEFAULT_HORIZON,
    runID: null,
    generationStartedAt: null,
    previewRevision: 1,
    progress: null,
    stage: null,               // 'building'|'generating'|null — server-reported work stage
    targets: [],
    peopleByID: new Map(),
    draft: {},                 // taskInstanceID -> [personID] additions only
    acks: new Set(),           // `${taskInstanceID}|${personID}|${code}`
    window: null,
    summary: null,
    expiresAt: null,
    validation: null,          // {token, draftHash, expiresAt}
    receipt: null,             // {count, deliveryWarning}
    error: null,               // {code, message}
    statusErrorCount: 0,       // consecutive transient poll failures
    resumed: false,            // attached to a run already active server-side
});

const readStatus = (state, data) => {
    // Late responses must never revive a canceled/dismissed run.
    if (state.phase !== 'generating') return state;
    const status = data?.status;
    const runID = data?.runID ?? state.runID;
    if (status === 'failed' || (AUTO_SCHEDULE_ACTIVE_STATUSES.includes(status) && data?.errorCode === 'invalid_model_response')) {
        return { ...state, runID, phase: 'failed', error: { code: data?.errorCode ?? null, message: COPY.modelFailure } };
    }
    if (AUTO_SCHEDULE_ACTIVE_STATUSES.includes(status)) {
        const progress = readProgress(data) ?? state.progress;
        // `stage` is absent on older deployments: 'building' = criteria/
        // context still assembling (no batches yet), 'generating' = batch
        // work live. Backward-compat: an active run reporting 0 of 0 groups
        // is still building — never show a dead "0 of 0" line.
        const stage = data?.stage === 'building' || data?.stage === 'generating'
            ? data.stage
            : (progress?.completed === 0 && progress?.total === 0 ? 'building' : null);
        // A transient errorCode (e.g. worker_error during a server-side
        // requeue) on an ACTIVE status means the run is retrying — keep the
        // working state. errorCode is otherwise honored only on terminal
        // 'failed' (and the invalid_model_response check above).
        return { ...state, runID, window: data.window ?? state.window, progress, stage };
    }
    if (status === 'ready') {
        const { draft, peopleByID, targets } = buildDraftState(data);
        if (targets.length === 0) return { ...state, runID, phase: 'empty' };
        return {
            ...state, runID, phase: 'preview',
            targets, peopleByID, draft,
            acks: new Set(),
            previewRevision: data.previewRevision ?? state.previewRevision,
            summary: data.summary ?? null,
            window: data.window ?? null,
            expiresAt: data.expiresAt ?? null,
            error: null,
        };
    }
    if (status === 'canceled') return { ...state, runID, phase: 'canceled' };
    if (status === 'expired') return { ...state, runID, phase: 'expired' };
    if (status === 'confirmed') return { ...state, runID, phase: 'success', receipt: readReceipt(data) };
    return { ...state, runID };
};

const mapDraftMutationError = (state, error, { onRetryable }) => {
    const code = error?.code ?? null;
    if (AUTO_SCHEDULE_STALE_CODES.includes(code)) return { ...state, phase: 'stale' };
    if (AUTO_SCHEDULE_EXPIRED_CODES.includes(code)) return { ...state, phase: 'expired' };
    if (code === 'validation_superseded') {
        return { ...state, phase: 'preview', validation: null, error: { code, message: error?.message } };
    }
    if (code === 'already_confirmed') {
        return { ...state, phase: 'failed', error: { code, message: 'This schedule was already confirmed.' } };
    }
    // New acknowledgable warnings (e.g. a do_not_suggest rule saved while
    // reviewing) send the user back to the preview to acknowledge — the draft
    // is preserved and stays confirmable.
    if (code === 'warnings_not_acknowledged') {
        return { ...state, phase: 'preview', validation: null, error: { code, message: COPY.warningsNeedAck } };
    }
    // Hard draft violations (existing assignee, declined occurrence,
    // non-acknowledgeable blocks, capacity…) stay blocking — but the draft is
    // kept so the user can remove the offending selection instead of losing
    // the whole review.
    if (code === 'invalid_draft' || code === 'hard_constraint_violation') {
        return { ...state, phase: 'preview', validation: null, error: { code, message: COPY.draftRejected } };
    }
    if (error?.retryable) {
        return { ...state, phase: onRetryable, error: { code, message: error?.message || 'The scheduler is busy — try again.' } };
    }
    return { ...state, phase: 'failed', error: { code, message: error?.message || 'Something went wrong with Auto-Schedule.' } };
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'RESET':
            return { ...makeInitial(), horizonDays: state.horizonDays };
        case 'SET_HORIZON':
            return { ...state, horizonDays: action.days };
        case 'GENERATE_START':
            return { ...state, phase: 'generating', error: null, progress: null, stage: null, runID: null, window: null, generationStartedAt: action.startedAt, statusErrorCount: 0, resumed: false };
        case 'GENERATE_ATTACH': {
            // The server reported an already-active run for this actor —
            // adopt it and let the existing poll query resume it rather than
            // surfacing the rate limit as a failure.
            return { ...state, phase: 'generating', runID: action.runID, window: action.window ?? state.window, error: null, statusErrorCount: 0, resumed: true };
        }
        case 'GENERATE_ERROR': {
            const e = action.error;
            const message = AUTO_SCHEDULE_MODEL_FAILURE_CODES.includes(e?.code) ? COPY.modelFailure : (e?.message || COPY.modelFailure);
            return { ...state, phase: 'failed', error: { code: e?.code ?? null, message } };
        }
        case 'STATUS': {
            const next = readStatus(state, action.data);
            // Any successful status response clears the transient-failure tally.
            return next === state ? state : { ...next, statusErrorCount: 0 };
        }
        case 'STATUS_ERROR': {
            if (state.phase !== 'generating') return state;
            const code = action.error?.code ?? null;
            if (code === 'preview_expired') return { ...state, phase: 'expired' };
            // Transient poll failures (suspended screen, dropped connection)
            // must not kill the run — stay generating; the query keeps
            // refetching while enabled and recovers on its own. The 15-min
            // deadline effect remains the final backstop.
            if (isTransientStatusError(action.error) && state.statusErrorCount + 1 < MAX_TRANSIENT_STATUS_ERRORS) {
                return { ...state, statusErrorCount: state.statusErrorCount + 1 };
            }
            return { ...state, phase: 'failed', error: { code, message: action.error?.message || COPY.modelFailure } };
        }
        case 'CANCELED':
            return ['generating', 'preview'].includes(state.phase) ? { ...state, phase: 'canceled' } : state;
        case 'EDIT': {
            const draft = { ...state.draft, [action.taskInstanceID]: action.personIDs };
            const peopleByID = new Map(state.peopleByID);
            (action.people || []).forEach(p => { if (p?.id) peopleByID.set(p.id, p); });
            // Any edit invalidates a bound validation token immediately.
            return { ...state, draft, peopleByID, validation: null, error: null };
        }
        case 'TOGGLE_ACK': {
            const acks = new Set(state.acks);
            const key = ackKey(action.taskInstanceID, action.personID, action.code);
            if (acks.has(key)) acks.delete(key); else acks.add(key);
            return { ...state, acks, validation: null };
        }
        case 'VALIDATE_START':
            return { ...state, phase: 'validating', error: null };
        case 'VALIDATE_OK':
            return { ...state, phase: 'confirmReview', validation: { token: action.token, draftHash: action.draftHash, expiresAt: action.expiresAt ?? null }, error: null };
        case 'VALIDATE_ERROR':
            return mapDraftMutationError(state, action.error, { onRetryable: 'preview' });
        case 'CONFIRM_START':
            return { ...state, phase: 'confirming', error: null };
        case 'CONFIRM_OK':
            return { ...state, phase: 'success', receipt: action.receipt, error: null };
        case 'CONFIRM_ERROR':
            return mapDraftMutationError(state, action.error, { onRetryable: 'confirmReview' });
        case 'EXPIRED':
            return { ...state, phase: 'expired' };
        case 'TO_SETUP':
            return { ...state, phase: 'setup', error: null, runID: null };
        case 'TO_PREVIEW':
            return { ...state, phase: 'preview', error: null };
        case 'PREVIEW_ERROR':
            return { ...state, phase: 'preview', error: action.error };
        default:
            return state;
    }
};

const formatCoveredDates = (timezone, horizonDays) => {
    const start = DateTime.now().setZone(timezone).startOf('day');
    const end = start.plus({ days: horizonDays });
    if (!start.isValid || !end.isValid) return null;
    return `${start.toFormat('LLL d')} – ${end.minus({ days: 1 }).toFormat('LLL d, yyyy')}`;
};

const formatWindow = (window, timezone) => {
    if (!window?.start || !window?.endExclusive) return null;
    const start = DateTime.fromISO(window.start).setZone(timezone).startOf('day');
    const end = DateTime.fromISO(window.endExclusive).setZone(timezone).minus({ days: 1 }).startOf('day');
    if (!start.isValid || !end.isValid) return null;
    return `${start.toFormat('LLL d')} – ${end.toFormat('LLL d, yyyy')}`;
};

const titleForPhase = (phase) => ['preview', 'validating', 'confirmReview', 'confirming'].includes(phase)
    ? COPY.previewTitle
    : COPY.setupTitle;

/**
 * Shared Auto-Schedule modal (spec §9.1/§9.2). One reducer owns the whole
 * state machine; no path can render a live Confirm while another mutating
 * request is in flight.
 */
export default function AutoScheduleModal({ isOpen, onClose, teamIDs = [], teamNames = [], eventID = null, eventName = null, source = 'managed-coverage' }) {
    const queryClient = useQueryClient();
    const { allowed: canUseAI, reason: aiReason, loggedInUser } = useAutoScheduleAccess();
    const { data: organization } = Query.organization.byId();
    const timezone = organization?.timezone || 'UTC';
    const orgID = loggedInUser?.organizationID;
    const personID = loggedInUser?.id;

    const [state, dispatch] = useReducer(reducer, undefined, makeInitial);
    const stateRef = useRef(state);
    stateRef.current = state;
    // One idempotency/key pair per modal session — never per render.
    // `pendingGenerate` scopes the clientRequestID to its logical request
    // (teamIDs|eventID|horizonDays) so a retry of the SAME request reuses the
    // idempotency key (server replays the run — no new insert, no rate limit);
    // it's cleared the moment any runID is received and on each modal open.
    const keysRef = useRef({ clientRequestID: null, idempotencyKey: null, pendingGenerate: null });

    useEffect(() => {
        if (isOpen) {
            // `source` is presentation/telemetry only — never sent as
            // authorization input to the edge function.
            keysRef.current = { clientRequestID: newId(), idempotencyKey: newId(), source };
            dispatch({ type: 'RESET' });
        }
    }, [isOpen, source]);

    const generateMutation = useMutation({ mutationFn: generateAutoSchedule, retry: false });
    const validateMutation = useMutation({ mutationFn: validateAutoSchedule, retry: false });
    const confirmMutation = useMutation({ mutationFn: confirmAutoSchedule, retry: false });
    const cancelMutation = useMutation({ mutationFn: cancelAutoSchedule, retry: false });

    // Polls only while generating — the interval itself comes from the
    // server-returned pollAfterMs and stops on terminal status.
    const previewQuery = Query.taskAutoSchedule.preview({
        orgID,
        personID,
        runID: state.runID,
        options: { enabled: isOpen && state.phase === 'generating' && !!state.runID && !!orgID && !!personID },
    });
    useEffect(() => {
        if (previewQuery.data) dispatch({ type: 'STATUS', data: previewQuery.data });
    }, [previewQuery.data]);
    useEffect(() => {
        if (previewQuery.error) dispatch({ type: 'STATUS_ERROR', error: previewQuery.error });
    }, [previewQuery.error]);

    // "Do not suggest" rules covering the preview's tasks — drives the
    // per-person menu toggle and merges 'do_not_suggest' into the
    // required-acknowledgment mirror (server emits it as a requiredWarning).
    const exclusionTaskIDs = useMemo(() => [...new Set(state.targets.map(t => t.taskID).filter(Boolean))], [state.targets]);
    const exclusionsQuery = Query.taskAutoSchedule.exclusions({
        orgID,
        personID,
        runID: state.runID,
        taskIDs: exclusionTaskIDs,
        options: { enabled: isOpen && ['preview', 'validating', 'confirmReview', 'confirming'].includes(state.phase) },
    });
    const exclusionMap = useMemo(() => {
        const map = new Map();
        (exclusionsQuery.data || []).forEach(x => {
            if (x?.taskID && x?.eventID && x?.personID && x?.id) {
                map.set(`${x.taskID}|${x.eventID}|${x.personID}`, x.id);
            }
        });
        return map;
    }, [exclusionsQuery.data]);
    const exclusionFor = useCallback((target, personID) => (
        target?.taskID && target?.eventID
            ? exclusionMap.get(`${target.taskID}|${target.eventID}|${personID}`)
            : undefined
    ), [exclusionMap]);
    const isExcluded = useCallback((target, personID) => !!exclusionFor(target, personID), [exclusionFor]);

    const exclusionMutation = useMutation({
        mutationFn: ({ target, person, exclusionID }) => exclusionID
            ? removeAutoScheduleExclusion({ exclusionID })
            : addAutoScheduleExclusion({ taskID: target.taskID, eventID: target.eventID, personID: person.id }),
        onSuccess: (_data, vars) => {
            const name = personLabel(vars.person);
            Toast.show({ type: 'success', text2: vars.exclusionID ? `${name} ${COPY.exclusionRemoved}` : `${name} ${COPY.exclusionSet}` });
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskAutoSchedule.exclusions(orgID, personID, stateRef.current.runID) });
        },
    });
    const handleToggleExclusion = useCallback((target, person, exclusionID) => {
        exclusionMutation.mutate({ target, person, exclusionID });
    }, [exclusionMutation]);

    // Missing worker sweeps or unchanged queued responses must not keep the
    // poller alive indefinitely. Progress updates never reset this deadline.
    useEffect(() => {
        if (!isOpen || state.phase !== 'generating') return;
        const serverStart = Date.parse(state.window?.start);
        const startedAt = Number.isFinite(serverStart)
            ? Math.min(state.generationStartedAt, serverStart)
            : state.generationStartedAt;
        const remaining = startedAt + AUTO_SCHEDULE_GENERATION_TIMEOUT_MS - Date.now();
        const fail = () => dispatch({
            type: 'STATUS_ERROR',
            error: { code: 'generation_deadline_exceeded', message: COPY.modelFailure },
        });
        if (remaining <= 0) { fail(); return; }
        const timer = setTimeout(fail, remaining);
        return () => clearTimeout(timer);
    }, [isOpen, state.phase, state.generationStartedAt, state.window?.start]);

    // Client-side expiry guard: an outlived draft can never be confirmed.
    useEffect(() => {
        if (state.phase !== 'preview' && state.phase !== 'confirmReview') return;
        if (!state.expiresAt) return;
        const remaining = new Date(state.expiresAt).getTime() - Date.now();
        if (remaining <= 0) { dispatch({ type: 'EXPIRED' }); return; }
        const timer = setTimeout(() => dispatch({ type: 'EXPIRED' }), remaining);
        return () => clearTimeout(timer);
    }, [state.phase, state.expiresAt]);

    const handleGenerate = useCallback(() => {
        const horizonDays = stateRef.current.horizonDays;
        const scopeKey = scopeKeyFor(teamIDs, eventID, horizonDays);
        // Retry of the SAME logical request (identical scope, prior attempt
        // never delivered a runID — e.g. the screen suspended mid-invoke)
        // reuses its clientRequestID so the server replays that run instead of
        // inserting a new one / hitting the active-run rate limit. A changed
        // scope or a first attempt mints a fresh key.
        const clientRequestID = keysRef.current.pendingGenerate?.scopeKey === scopeKey
            ? keysRef.current.pendingGenerate.clientRequestID
            : newId();
        keysRef.current.clientRequestID = clientRequestID;
        keysRef.current.pendingGenerate = { scopeKey, clientRequestID };
        dispatch({ type: 'GENERATE_START', startedAt: Date.now() });
        generateMutation.mutate(
            { clientRequestID, teamIDs, eventID, horizonDays },
            {
                onSuccess: (data) => {
                    // A runID was received — never reuse this key again.
                    if (data?.runID) keysRef.current.pendingGenerate = null;
                    dispatch({ type: 'STATUS', data });
                    // User may cancel before generate resolves; once the runID
                    // finally arrives the server-side job still needs canceling.
                    if (stateRef.current.phase === 'canceled' && data?.runID) {
                        cancelMutation.mutate({ runID: data.runID });
                    }
                },
                onError: (error) => {
                    // rate_limited + active_generation hint: a run for this
                    // actor is still generating server-side — attach to it and
                    // let the poll resume instead of showing a failure.
                    const activeRunID = error?.details?.activeRunID;
                    if (error?.code === 'rate_limited'
                        && error?.details?.hint === 'active_generation'
                        && UUID_RE.test(activeRunID || '')) {
                        keysRef.current.pendingGenerate = null;
                        dispatch({ type: 'GENERATE_ATTACH', runID: activeRunID, window: error.details.window ?? null });
                        return;
                    }
                    dispatch({ type: 'GENERATE_ERROR', error });
                },
            },
        );
    }, [generateMutation, cancelMutation, teamIDs, eventID]);

    const handleCancelGeneration = useCallback(() => {
        const runID = stateRef.current.runID;
        dispatch({ type: 'CANCELED' });
        if (runID) cancelMutation.mutate({ runID });
    }, [cancelMutation]);

    const handleClose = useCallback(() => {
        const s = stateRef.current;
        // Dismissing an active run cancels it so late work can't be confirmed.
        if (s.runID && ['generating', 'preview', 'confirmReview', 'validating'].includes(s.phase)) {
            cancelMutation.mutate({ runID: s.runID });
        }
        queryClient.removeQueries({ queryKey: QueryKeys.taskAutoSchedule.all(orgID, personID) });
        onClose?.();
    }, [cancelMutation, queryClient, orgID, personID, onClose]);

    const handleEdit = useCallback((taskInstanceID, items) => {
        // SheetSelector may hand back raw person objects or {original} wraps.
        const people = (items || []).map(i => i?.original || i).filter(Boolean);
        dispatch({ type: 'EDIT', taskInstanceID, personIDs: people.map(p => p.id).filter(Boolean), people });
    }, []);

    const handleToggleAck = useCallback((taskInstanceID, personID, code) => {
        dispatch({ type: 'TOGGLE_ACK', taskInstanceID, personID, code });
    }, []);

    const conflicts = useMemo(() => computeDraftConflicts(state.targets, state.draft), [state.targets, state.draft]);
    const requiredAcks = useMemo(() => requiredAcknowledgments(state.targets, state.draft, isExcluded), [state.targets, state.draft, isExcluded]);
    const missingAcks = useMemo(() => missingAcknowledgments(state.targets, state.draft, state.acks, isExcluded), [state.targets, state.draft, state.acks, isExcluded]);
    const requiredAcksByTask = useMemo(() => {
        const map = new Map();
        requiredAcks.forEach(r => {
            if (!map.has(r.taskInstanceID)) map.set(r.taskInstanceID, []);
            map.get(r.taskInstanceID).push(r);
        });
        return map;
    }, [requiredAcks]);
    const overCapacity = useMemo(() => state.targets.some(t => Number.isFinite(t.openCount) && (state.draft[t.taskInstanceID] || []).length > t.openCount), [state.targets, state.draft]);
    const additionCount = useMemo(() => draftAdditionCount(state.draft), [state.draft]);
    const eventCount = useMemo(() => new Set(state.targets.map(t => t.eventInstanceID)).size, [state.targets]);
    const remainingCount = useMemo(() => state.targets.reduce((sum, t) => {
        const open = Number.isFinite(t.openCount) ? t.openCount : (state.draft[t.taskInstanceID] || []).length;
        return sum + Math.max(0, open - (state.draft[t.taskInstanceID] || []).length);
    }, 0), [state.targets, state.draft]);
    const isEditedTask = useCallback((target) => {
        const proposed = target.proposed.map(p => p.person.id).join('|');
        return (state.draft[target.taskInstanceID] || []).join('|') !== proposed;
    }, [state.draft]);

    const confirmBlockReason = useMemo(() => {
        if (!canUseAI) return aiReason;
        // Double-booking is no longer a hard block: conflicts surface as
        // 'cross_target_conflict' acknowledgment chips (below) instead.
        if (missingAcks.length > 0) return 'Acknowledge every highlighted warning first.';
        if (overCapacity) return 'Remove extra people from over-filled tasks.';
        if (additionCount === 0) return 'No suggested assignments to confirm.';
        return null;
    }, [canUseAI, aiReason, missingAcks, overCapacity, additionCount]);

    const handleConfirmPress = useCallback(() => {
        const s = stateRef.current;
        if (s.phase !== 'preview' || confirmBlockReason) return;
        dispatch({ type: 'VALIDATE_START' });
        const currentHash = draftHash(s.targets, s.draft);
        validateMutation.mutate(
            {
                runID: s.runID,
                previewRevision: s.previewRevision,
                assignments: validatePayload(s.targets, s.draft),
                acknowledgedWarnings: acknowledgedWarningsPayload(s.acks, requiredAcks),
            },
            {
                onSuccess: (data) => dispatch({
                    type: 'VALIDATE_OK',
                    token: data?.validationToken,
                    // The server returns ITS own draftHash for its binding;
                    // client-side change detection always uses our canonical hash.
                    draftHash: currentHash,
                    expiresAt: data?.validationExpiresAt ?? data?.expiresAt ?? null,
                }),
                onError: (error) => {
                    // A do_not_suggest rule saved elsewhere (or just now in
                    // this session) shows up as a new required warning —
                    // refresh the local exclusion set so the chip appears.
                    if (error?.code === 'warnings_not_acknowledged') {
                        queryClient.invalidateQueries({ queryKey: QueryKeys.taskAutoSchedule.exclusions(orgID, personID, s.runID) });
                    }
                    dispatch({ type: 'VALIDATE_ERROR', error });
                },
            },
        );
    }, [validateMutation, confirmBlockReason, requiredAcks, queryClient, orgID, personID]);

    const handleFinalConfirm = useCallback(() => {
        const s = stateRef.current;
        const validation = s.validation;
        if (s.phase !== 'confirmReview' || !validation?.token) return;
        // The token is bound to the exact draft that was validated.
        if (validation.draftHash !== draftHash(s.targets, s.draft)) {
            dispatch({ type: 'PREVIEW_ERROR', error: { code: 'draft_changed', message: 'The draft changed since it was reviewed. Confirm again.' } });
            return;
        }
        dispatch({ type: 'CONFIRM_START' });
        confirmMutation.mutate(
            { runID: s.runID, validationToken: validation.token, idempotencyKey: keysRef.current.idempotencyKey },
            {
                onSuccess: (data) => {
                    const receipt = readReceipt(data);
                    dispatch({ type: 'CONFIRM_OK', receipt });
                    // Refresh every surface that shows assignments/coverage.
                    const affectedPeople = new Set();
                    const affectedTeams = new Set(s.teamIDs || teamIDs);
                    s.targets.forEach(t => {
                        if (t.teamID) affectedTeams.add(t.teamID);
                        (s.draft[t.taskInstanceID] || []).forEach(pid => affectedPeople.add(pid));
                        queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(t.taskInstanceID) });
                        if (t.eventID) queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(t.eventID) });
                    });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks() });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments() });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.taskTeams.all() });
                    affectedTeams.forEach(tid => queryClient.invalidateQueries({ queryKey: QueryKeys.taskTeams.byId(tid) }));
                    affectedPeople.forEach(pid => queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(pid) }));
                    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
                },
                onError: (error) => dispatch({ type: 'CONFIRM_ERROR', error }),
            },
        );
    }, [confirmMutation, queryClient, teamIDs]);

    // ── Phase content ────────────────────────────────────────────────────────
    const coveredDates = formatCoveredDates(timezone, state.horizonDays);
    const previewDates = formatWindow(state.window, timezone);
    const pending = generateMutation.isPending || validateMutation.isPending || confirmMutation.isPending;

    const setupContent = (
        <View style={styles.section}>
            <Text style={styles.fieldLabel}>{COPY.horizonLabel}</Text>
            <SegmentedTabs
                testIDPrefix="AutoScheduleHorizon"
                tabs={AUTO_SCHEDULE_HORIZONS.map(days => ({ key: days, label: `${days} days` }))}
                value={state.horizonDays}
                onChange={(days) => dispatch({ type: 'SET_HORIZON', days })}
            />
            {!!coveredDates && <Text style={styles.scopeLine}>Covers {coveredDates} ({timezone})</Text>}
            <Text style={styles.scopeLine} numberOfLines={3}>Teams: {teamNames.length ? teamNames.join(', ') : 'None selected'}</Text>
            {!!eventName && <Text style={styles.scopeLine} numberOfLines={2}>Event: {eventName}</Text>}
            <Text style={styles.explanation}>{COPY.explanation}</Text>
            <View style={styles.noteRow}>
                <Unicon name="lock-closed-outline" size={13} colorVariant="textSecondary" />
                <Text style={styles.noteText}>{COPY.existingNote}</Text>
            </View>
            {!!aiReason && <Text style={styles.errorText} accessibilityLiveRegion="polite">{aiReason}</Text>}
            {teamIDs.length === 0 && <Text style={styles.errorText}>No teams in scope for Auto-Schedule.</Text>}
            <View style={styles.actions}>
                <SolidButton
                    testID="AutoScheduleGenerate"
                    text={COPY.generateAction}
                    iconName="sparkles-outline"
                    iconBefore
                    disabled={!canUseAI || teamIDs.length === 0 || pending}
                    onPress={handleGenerate}
                />
                <SolidButton testID="AutoScheduleCancel" text={COPY.cancelAction} variant="muted" type="textSecondary" onPress={handleClose} />
            </View>
        </View>
    );

    const generatingContent = (
        <View style={styles.centeredSection}>
            <ActivityIndicator size="large" />
            <Text style={styles.phaseHeading}>{COPY.generatingHeading}</Text>
            <Text style={styles.phaseDetail}>{state.resumed ? COPY.resumedDetail : COPY.generatingDetail}</Text>
            <Text testID="AutoScheduleProgress" accessibilityLiveRegion="polite" style={styles.progressText}>
                {state.stage === 'building'
                    ? COPY.buildingDetail
                    : state.progress ? progressLine(state.progress.completed, state.progress.total) : 'Starting…'}
            </Text>
            <SolidButton testID="AutoScheduleCancel" text={COPY.cancelGeneration} variant="muted" type="textSecondary" onPress={handleCancelGeneration} disabled={cancelMutation.isPending} />
        </View>
    );

    const messagePhase = (icon, text, extra = null) => (
        <View style={styles.centeredSection} testID="AutoScheduleMessage">
            <Unicon name={icon} size={30} colorVariant="textSecondary" />
            <Text style={styles.phaseDetail} accessibilityLiveRegion="polite">{text}</Text>
            {extra}
            <View style={styles.actions}>
                <SolidButton testID="AutoScheduleRefresh" text={COPY.newPreview} iconName="refresh" iconBefore onPress={() => dispatch({ type: 'TO_SETUP' })} />
                <SolidButton text="Close" variant="muted" type="textSecondary" onPress={handleClose} />
            </View>
        </View>
    );

    const previewContent = (
        <View style={styles.previewRoot}>
            <View style={styles.previewHeader}>
                <Text style={styles.summaryText}>{summaryLine(state.summary?.proposedCount ?? additionCount, state.summary?.eventCount ?? eventCount)}</Text>
                {(state.summary?.remainingCount ?? remainingCount) > 0 && <Text style={styles.gapText}>{gapLine(state.summary?.remainingCount ?? remainingCount)}</Text>}
                {!!previewDates && <Text style={styles.scopeLine}>{previewDates} ({timezone})</Text>}
                <View style={styles.notice}>
                    <Unicon name="information-circle-outline" size={14} colorVariant="tasks" />
                    <Text style={styles.noticeText}>{COPY.nothingAssigned}</Text>
                </View>
            </View>
            <ScrollView style={styles.previewScroll} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
                <AutoSchedulePreview
                    targets={state.targets}
                    draft={state.draft}
                    peopleByID={state.peopleByID}
                    acks={state.acks}
                    conflicts={conflicts}
                    pendingAcksByTask={requiredAcksByTask}
                    isEditedTask={isEditedTask}
                    onEdit={handleEdit}
                    onToggleAck={handleToggleAck}
                    exclusionFor={exclusionFor}
                    onToggleExclusion={handleToggleExclusion}
                    timezone={timezone}
                />
            </ScrollView>
            {!!state.error && <Text style={styles.errorText} accessibilityLiveRegion="polite">{state.error.message}</Text>}
            {!!confirmBlockReason && !state.error && <Text style={styles.hintText}>{confirmBlockReason}</Text>}
            <View style={styles.actions}>
                <SolidButton text={COPY.back} variant="muted" type="textSecondary" onPress={() => dispatch({ type: 'TO_SETUP' })} />
                <SolidButton testID="AutoScheduleCancel" text={COPY.cancelAction} variant="muted" type="textSecondary" onPress={handleClose} />
                <View style={styles.grow} />
                <SolidButton
                    testID="AutoScheduleConfirm"
                    text={confirmLabel(additionCount)}
                    type="success"
                    disabled={!!confirmBlockReason || pending}
                    onPress={handleConfirmPress}
                />
            </View>
        </View>
    );

    const confirmReviewContent = (
        <View style={styles.centeredSection}>
            <Unicon name="checkmark-circle-outline" size={30} colorVariant="tasks" />
            <Text style={styles.phaseHeading}>{confirmLabel(additionCount)}</Text>
            <Text style={styles.phaseDetail}>{additionCount} assignment{additionCount === 1 ? '' : 's'} ready to create.</Text>
            <Text style={styles.phaseDetail}>{COPY.notificationsNote}</Text>
            {!!state.error && <Text style={styles.errorText} accessibilityLiveRegion="polite">{state.error.message}</Text>}
            <View style={styles.actions}>
                <SolidButton text={COPY.back} variant="muted" type="textSecondary" onPress={() => dispatch({ type: 'TO_PREVIEW' })} />
                <SolidButton
                    testID="AutoScheduleConfirm"
                    text={confirmLabel(additionCount)}
                    type="success"
                    disabled={pending}
                    onPress={handleFinalConfirm}
                />
            </View>
        </View>
    );

    const busyContent = (label) => (
        <View style={styles.centeredSection}>
            <ActivityIndicator size="large" />
            <Text style={styles.phaseDetail} accessibilityLiveRegion="polite">{label}</Text>
        </View>
    );

    const successContent = (
        <View style={styles.centeredSection} testID="AutoScheduleSuccess">
            <Unicon name="checkmark-circle" size={34} colorVariant="success" />
            <Text style={styles.phaseHeading}>{successLine(state.receipt?.count ?? additionCount)}</Text>
            {state.receipt?.deliveryWarning && <Text style={styles.errorText} accessibilityLiveRegion="polite">{COPY.deliveryWarning}</Text>}
            <SolidButton text="Done" onPress={handleClose} />
        </View>
    );

    const content = {
        setup: setupContent,
        generating: generatingContent,
        preview: previewContent,
        validating: busyContent('Checking the schedule…'),
        confirmReview: confirmReviewContent,
        confirming: busyContent('Creating assignments…'),
        success: successContent,
        empty: messagePhase('calendar-outline', COPY.noTasks),
        failed: messagePhase('alert-circle-outline', state.error?.message || COPY.modelFailure),
        stale: messagePhase('refresh-outline', COPY.stale),
        expired: messagePhase('time-outline', COPY.expired),
        canceled: messagePhase('close-circle-outline', COPY.canceled),
    }[state.phase] || setupContent;

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={handleClose}
            title={titleForPhase(state.phase)}
            width={['preview', 'validating', 'confirmReview', 'confirming'].includes(state.phase) ? 1100 : 640}
            scrollable={false}
            cancelText="Close"
        >
            <View testID="AutoScheduleModal" style={styles.body}>
                {content}
            </View>
        </SheetModal>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    body: { flex: 1, padding: 16, gap: 12 },
    section: { gap: 12 },
    centeredSection: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, paddingVertical: 30 },
    fieldLabel: { color: colors.text, fontFamily: fonts.heading, fontSize: 14 },
    scopeLine: { color: colors.textSecondary, fontSize: 12 },
    explanation: { color: colors.textSecondary, fontSize: 13, lineHeight: 19 },
    noteRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 6, backgroundColor: colors.surfaceVariant, borderRadius: 10, padding: 10 },
    noteText: { flex: 1, color: colors.textSecondary, fontSize: 12, lineHeight: 17 },
    actions: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' },
    grow: { flex: 1 },
    phaseHeading: { color: colors.text, fontFamily: fonts.heading, fontSize: 17 },
    phaseDetail: { color: colors.textSecondary, fontSize: 13, textAlign: 'center', lineHeight: 19 },
    progressText: { color: colors.tasks, fontFamily: fonts.bold, fontSize: 13 },
    previewRoot: { flex: 1, gap: 10, minHeight: 0 },
    previewHeader: { gap: 4 },
    summaryText: { color: colors.text, fontFamily: fonts.heading, fontSize: 14 },
    gapText: { color: colors.warning, fontSize: 12 },
    notice: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.tasksContainer, borderRadius: 9, padding: 8, marginTop: 2 },
    noticeText: { color: colors.tasks, fontFamily: fonts.bold, fontSize: 12 },
    previewScroll: { flex: 1, minHeight: 0 },
    errorText: { color: colors.error, fontSize: 12, lineHeight: 17 },
    hintText: { color: colors.textSecondary, fontSize: 12 },
}));
